// FeedbackInbox — operator-only view of user-submitted feedback.
//
// Today this is a flat list with status filtering + inline status changes.
// It's intentionally the *starter* for a ticketing system: status is already
// a tri-state, the data model has assignedTo / notes / resolvedAt fields,
// and the RTDB rules let any signed-in user create while operators get
// read+write — that's the full ticket primitive. Future passes will add
// a detail drawer, assignment picker, notes thread, etc.

import { useEffect, useMemo, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';

import {
  FeedbackRecord,
  listFeedback,
  setFeedbackStatus,
} from '../firebase';
import '../pm/theme.css';

type StatusFilter = 'all' | 'open' | 'in_progress' | 'closed';

const STATUS_LABEL: Record<FeedbackRecord['status'], string> = {
  open: 'Open',
  in_progress: 'In progress',
  closed: 'Closed',
};

// Pill color per status. Pulled from the existing PM token palette so the
// inbox feels native to the operator surface instead of inventing new hues.
const STATUS_COLOR: Record<FeedbackRecord['status'], { bg: string; fg: string }> = {
  open:        { bg: 'rgba(176, 0, 32, 0.10)',   fg: 'var(--rr-danger, #b00020)' },
  in_progress: { bg: 'rgba(180, 130, 0, 0.12)',  fg: '#8a6300' },
  closed:      { bg: 'rgba(20, 130, 60, 0.10)',  fg: 'var(--rr-success, #14823c)' },
};

const FILTERS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'closed', label: 'Closed' },
];

function formatTimestamp(ts: number): string {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return new Date(ts).toISOString();
  }
}

const FeedbackInbox: React.FC = () => {
  const [records, setRecords] = useState<FeedbackRecord[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('open');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const unsub = listFeedback(
      (recs) => {
        setRecords(recs);
        setLoadError(null);
      },
      (err) => {
        setLoadError(
          err.message ||
            'Could not load feedback. You may not have operator access.',
        );
      },
    );
    return unsub;
  }, []);

  const counts = useMemo(() => {
    const c = { all: records.length, open: 0, in_progress: 0, closed: 0 };
    for (const r of records) {
      if (r.status === 'open') c.open += 1;
      else if (r.status === 'in_progress') c.in_progress += 1;
      else if (r.status === 'closed') c.closed += 1;
    }
    return c;
  }, [records]);

  const visible = useMemo(() => {
    if (filter === 'all') return records;
    return records.filter((r) => r.status === filter);
  }, [records, filter]);

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function changeStatus(id: string, status: FeedbackRecord['status']) {
    setBusyIds((prev) => new Set(prev).add(id));
    try {
      await setFeedbackStatus(id, status);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('setFeedbackStatus failed', err);
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="pm-app">
          <div className="pm-page" style={{ maxWidth: 960, padding: '24px 28px' }}>
            <div className="pm-header">
              <div className="pm-header-titles">
                <h1>Feedback inbox</h1>
                <p>
                  User-submitted feedback. Open tickets here become support
                  tickets as we build out the full triage flow.
                </p>
              </div>
            </div>

            {/* Filter row — count chips per status. Active filter gets the
                primary background, others stay ghost. */}
            <div
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                marginTop: 12,
                marginBottom: 16,
              }}
            >
              {FILTERS.map((f) => {
                const active = filter === f.id;
                const count = counts[f.id];
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFilter(f.id)}
                    className="pm-button"
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      background: active
                        ? 'var(--rr-ink, #111)'
                        : 'transparent',
                      color: active ? '#fff' : 'var(--rr-ink, #111)',
                      border: '1px solid var(--pm-border, var(--rr-stone-300))',
                      fontWeight: 500,
                    }}
                  >
                    {f.label} ({count})
                  </button>
                );
              })}
            </div>

            {loadError ? (
              <div
                role="alert"
                style={{
                  padding: '12px 14px',
                  borderRadius: 8,
                  background: 'rgba(176, 0, 32, 0.08)',
                  color: 'var(--rr-danger, #b00020)',
                }}
              >
                {loadError}
              </div>
            ) : null}

            {!loadError && visible.length === 0 ? (
              <div
                style={{
                  padding: '32px 12px',
                  textAlign: 'center',
                  color: 'var(--rr-muted, #777)',
                }}
              >
                No feedback {filter === 'all' ? 'yet' : `with status "${STATUS_LABEL[filter as FeedbackRecord['status']] || filter}"`}.
              </div>
            ) : null}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {visible.map((r) => {
                const isOpen = expanded.has(r.id);
                const busy = busyIds.has(r.id);
                const sc = STATUS_COLOR[r.status];
                return (
                  <div
                    key={r.id}
                    // Hand-rolled card chrome — .pm-stat-card depends on
                    // a parent .pm-stat-cards grid for its background +
                    // border, which we deliberately don't use here.
                    style={{
                      padding: 14,
                      background: 'var(--rr-paper, #fff)',
                      border: '1px solid var(--pm-border, var(--rr-stone-300))',
                      borderRadius: 'var(--pm-radius-lg, 12px)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginBottom: 4,
                            flexWrap: 'wrap',
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: sc.bg,
                              color: sc.fg,
                            }}
                          >
                            {STATUS_LABEL[r.status]}
                          </span>
                          <span style={{ fontSize: 13, color: 'var(--rr-muted, #777)' }}>
                            {formatTimestamp(r.createdAt)}
                          </span>
                          <span style={{ fontSize: 13, color: 'var(--rr-ink, #111)' }}>
                            {r.userEmail || '(unknown)'}
                          </span>
                        </div>
                        <div
                          style={{
                            // Collapsed: clamp to 2 lines so the list stays
                            // scannable. Expanded: full text + context.
                            display: '-webkit-box',
                            WebkitLineClamp: isOpen ? 'unset' : 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: isOpen ? 'visible' : 'hidden',
                            whiteSpace: isOpen ? 'pre-wrap' : 'normal',
                            color: 'var(--rr-ink, #111)',
                            fontSize: 14,
                            lineHeight: 1.5,
                          }}
                        >
                          {r.message}
                        </div>
                        {isOpen && r.context ? (
                          <div
                            style={{
                              marginTop: 8,
                              padding: '8px 10px',
                              background: 'var(--rr-stone-100, #f5f4f1)',
                              borderRadius: 6,
                              fontSize: 12,
                              color: 'var(--rr-muted, #555)',
                              wordBreak: 'break-all',
                            }}
                          >
                            <strong style={{ fontWeight: 600 }}>Submitted from:</strong>{' '}
                            {r.context}
                          </div>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="pm-link-button"
                        onClick={() => toggleExpanded(r.id)}
                        style={{ flexShrink: 0 }}
                      >
                        {isOpen ? 'Collapse' : 'Expand'}
                      </button>
                    </div>

                    {/* Status transition row — appears when expanded so the
                        collapsed list stays minimal. Disabled while a write
                        is in flight so double-clicks don't race. */}
                    {isOpen ? (
                      <div
                        style={{
                          marginTop: 12,
                          display: 'flex',
                          gap: 6,
                          flexWrap: 'wrap',
                          paddingTop: 10,
                          borderTop: '1px solid var(--pm-border, var(--rr-stone-300))',
                        }}
                      >
                        <span style={{ alignSelf: 'center', fontSize: 12, color: 'var(--rr-muted, #777)', marginRight: 4 }}>
                          Set status:
                        </span>
                        {(['open', 'in_progress', 'closed'] as FeedbackRecord['status'][]).map((s) => {
                          const current = r.status === s;
                          return (
                            <button
                              key={s}
                              type="button"
                              className="pm-button"
                              disabled={current || busy}
                              onClick={() => changeStatus(r.id, s)}
                              style={{
                                padding: '4px 10px',
                                borderRadius: 6,
                                background: current ? 'var(--rr-ink, #111)' : 'transparent',
                                color: current ? '#fff' : 'var(--rr-ink, #111)',
                                border: '1px solid var(--pm-border, var(--rr-stone-300))',
                                opacity: busy ? 0.6 : 1,
                                fontSize: 13,
                              }}
                            >
                              {STATUS_LABEL[s]}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default FeedbackInbox;
